





























































































































































import axios from "@/libs/axios";
import { computed, ref } from "@vue/composition-api";
import { watchDebounced } from "@vueuse/core";
import {
    BButton,
    BFormCheckbox,
    BFormCheckboxGroup,
    BFormGroup,
    BFormSelect,
    BFormSelectOption,
    BOverlay,
    BSpinner,
} from "bootstrap-vue";
import { useToast } from "vue-toastification/composition";
import i18n from "@/libs/i18n";
import { wrapWithRetryHandling } from "@/utils/wrapWithRetryHandling.util";
import { useChannelsStore } from "@/store/useChannelsStore";
import { useChatsStore } from "@/store/useChatsStore";
import { resolveRemainingCustomerCareWindow } from "@/utils/chats";
import PromptOption from "./promptOption.vue";
import { TextEntryDto } from "@saysimple-platform/dtos";

export default {
    components: {
        PromptOption,
        BFormGroup,
        BFormSelect,
        BFormSelectOption,
        BFormCheckbox,
        BFormCheckboxGroup,
        BButton,
        BSpinner,
        BOverlay,
    },
    props: {
        messagebarContent: {
            type: String,
            required: false,
        },
    },
    setup({ messagebarContent }: { messagebarContent: string }, { emit, root }) {
        const toast = useToast();

        const aiOutput = ref("");

        const isDisabled = ref(false);

        const copyOutput = async () => {
            navigator?.clipboard?.writeText(aiOutput.value).catch((err) => {
                toast.error(i18n.t("chatgpt.error.copy-error"));
            });
        };

        const useInMessageBar = () => {
            if (!aiOutput.value) {
                toast.info(i18n.t("chatgpt.error.no-output"));
                return;
            }

            copyOutput();
            emit("copy-to-messagebar", aiOutput.value);
            root.$bvModal.hide("chat-gpt-modal");
        };

        const userInput = ref(messagebarContent);

        const promptOptionsData = [
            { instruction: "make-formal", icon: ["fas", "user-tie"] },
            { instruction: "make-informal", icon: ["fas", "face-smile-wink"] },
            { instruction: "rewrite-message", icon: ["fas", "message-pen"] },
            { instruction: "translate-message", icon: ["fas", "language"] },
            { instruction: "chat-summary", icon: ["fas", "notes"] },
        ];

        const instruction = ref("");

        //The selected option in the 'select-language' combobox
        const selectedLanguageOption = ref("");
        //The typed value in the 'language-textarea' textarea
        const typedLanguageOption = ref("");

        const availableLanguages = ["Dutch", "English", "French", "German", "Italian", "Portuguese", "Spanish"].map(
            (language) => ({
                text: i18n.t(`chatgpt.translate-language-select.${language.toLowerCase()}`),
                value: language,
            })
        );

        //If the user selected a language from the combobox, we can return that language. Otherwise, return the value
        // they typed in the textarea
        const assignSelectedLanguage = () => {
            if (selectedLanguageOption.value !== "other") {
                return selectedLanguageOption.value;
            }
            return typedLanguageOption.value;
        };

        const compatibleModels = [
            {
                text: "gpt-3.5-turbo",
                value: "gpt-3.5-turbo",
            },
        ];

        const selectedModel = ref("gpt-3.5-turbo");

        const requestTokens = ref("-");
        const responseTokens = ref("-");
        const totalTokens = ref("-");
        const userInputTokens = ref("-");

        const calculatingTokens = ref(false);

        const sendPrompt = async () => {
            if (instruction.value !== "chat-summary" && !userInput.value) {
                toast.error(i18n.t("chatgpt.error.no-input"));
                return;
            }
            if (
                instruction.value === "translate-message" &&
                (!selectedLanguageOption.value ||
                    (selectedLanguageOption.value === "other" && !typedLanguageOption.value))
            ) {
                toast.error(i18n.t("chatgpt.error.no-language"));
                return;
            }
            if (instruction.value === "chat-summary" && selectedMessageIds.value.length === 0) {
                toast.error(i18n.t("chatgpt.error.no-selected-messages"));
                return;
            }

            isDisabled.value = true;
            await wrapWithRetryHandling(
                () => {
                    return axios.post(`${process.env.VUE_APP_PUBLIC_API}/open-ai/prompt`, {
                        model: selectedModel.value,
                        instruction: instruction.value,
                        userInput: userInput.value,
                        language: assignSelectedLanguage(),
                        chatId: chatsStore.activeChat.chat.id,
                        selectedMessageIds: selectedMessageIds.value,
                    });
                },
                3,
                (error) => {
                    if (
                        error.response.status === 401 ||
                        error.response.status === 400 ||
                        error.response.status === 403
                    ) {
                        throw error;
                    }
                }
            )
                .then((result) => {
                    let output = result.data.choices[0].message.content;
                    if (output.match(/^"(.+?)"$/)) {
                        output = output.replace(/^"/, "").replace(/"$/, "");
                    }
                    aiOutput.value = output;
                    requestTokens.value = result.data.usage.prompt_tokens;
                    responseTokens.value = result.data.usage.completion_tokens;
                    totalTokens.value = result.data.usage.total_tokens;
                })
                .catch((error) => {
                    switch (error.response.status) {
                        case 401:
                            toast.error(i18n.t("chatgpt.error.api-key"));
                            break;
                        case 400:
                            toast.error(i18n.t("chatgpt.error.bad-request"));
                            break;
                        case 429:
                            toast.error(i18n.t("chatgpt.error.rate-limit-reached"));
                            break;
                        case 403:
                            toast.error(i18n.t("chatgpt.error.unauthorized"));
                            break;
                        default:
                            toast.error(i18n.t("chatgpt.error.server-error"));
                            break;
                    }
                })
                .finally(() => {
                    isDisabled.value = false;
                });
        };

        const chatsStore = useChatsStore();
        const textEntries = computed(() => {
            return chatsStore.activeChat.entries.filter((entry: TextEntryDto) => entry.message?.length > 0);
        });

        const selectedMessageIds = ref(textEntries.value.map((entry) => entry.id));

        const allMessagesSelected = computed(() => {
            return selectedMessageIds.value.length === textEntries.value.length;
        });

        const chatFilterOptions = computed(() => {
            return textEntries.value.map((entry: TextEntryDto) => {
                return { text: entry.message, value: entry.id };
            });
        });

        const channelsStore = useChannelsStore();

        const customerCareWindow = computed(() =>
            resolveRemainingCustomerCareWindow(
                chatsStore.activeChat?.chat.lastInboundMessageTime,
                channelsStore.activeChatChannel?.customerCareWindowLength
            )
        );

        const toggleAll = (checked) => {
            selectedMessageIds.value = checked
                ? textEntries.value.map((entry) => {
                      return entry.id;
                  })
                : [];
        };

        watchDebounced(
            [userInput, selectedMessageIds, instruction],
            async () => {
                calculatingTokens.value = true;
                let input: string;
                if (instruction.value == "chat-summary") {
                    input = textEntries.value
                        .filter((entry) => selectedMessageIds.value.includes(entry.id))
                        .map((entry: TextEntryDto) => entry.message)
                        .join("\n");
                } else {
                    input = userInput.value;
                }

                if (!input) {
                    calculatingTokens.value = false;
                    userInputTokens.value = "-";
                    return;
                }

                await axios
                    .post(`${process.env.VUE_APP_PUBLIC_API}/open-ai/tokenize`, {
                        input,
                    })
                    .then((result) => {
                        userInputTokens.value = result.data.tokens;
                    })
                    .catch((error) => {
                        userInputTokens.value = `- ${i18n.t("chatgpt.error.tokenizer")}`;
                    })
                    .finally(() => {
                        calculatingTokens.value = false;
                    });
            },
            { debounce: 500, maxWait: 5000 }
        );

        return {
            userInput,
            promptOptionsData,
            instruction,
            selectedModel,
            selectedLanguageOption,
            typedLanguageOption,
            availableLanguages,

            compatibleModels,

            aiOutput,
            requestTokens,
            responseTokens,
            totalTokens,
            userInputTokens,
            calculatingTokens,

            copyOutput,
            useInMessageBar,

            chatFilterOptions,
            selectedMessageIds,
            allMessagesSelected,
            toggleAll,

            sendPrompt,

            isDisabled,
            customerCareWindow,
        };
    },
};
